/**
 * マスタ管理 → 支払い方法タブ(2026-08-26 再構成)。
 * - 会社ごとに枠を分けて表示し、枠は「+/−」で開閉できる
 * - 列: 会社 / 種別 / 名称 / 残高 / 状態 / 編集
 * - 追加は「新規追加」ポップアップから(会社・種別・名称・期首残高)
 * - 編集ポップアップで名称・種別・会社・期首残高・有効/無効を設定できる
 * 実体は口座マスタ(cash_accounts)。CF(資金繰り)の残高管理と同じデータを共有する。
 * 残高は「期首残高+確定(settled)の入出金」の都度算出(CFの閲覧権限がない場合は期首残高を表示)
 */
import { useCallback, useEffect, useState } from 'react';
import type { HolidayShift } from '@core/lib/types';
import Modal from '@core/components/Modal';
import { useToast } from '@core/components/Toast';
import { useMasters } from './MastersProvider';
import {
  calcBalance,
  createCashAccount,
  fetchCashAccounts,
  fetchCashEntries,
  updateCashAccount,
} from '@core/cash/cashRepo';
import { formatAmountInput, formatYen } from '@core/lib/money';
import { debitRuleLabel, debitShiftLabel } from '@core/lib/cardDebit';
import {
  CASH_ACCOUNT_KINDS,
  CASH_ACCOUNT_KIND_LABELS,
  type CashAccount,
  type CashAccountKind,
  type Yen,
} from '@core/lib/types';

type EditState = {
  id: string | null; // null = 新規追加
  companyId: string;
  kind: CashAccountKind;
  name: string;
  openingText: string;
  active: boolean;
  /** クレカのみ(2026-09-04): 締め日・引き落とし月・引き落とし日(31=月末) */
  closingDay: string;
  debitOffset: '1' | '2';
  debitDay: string;
  /** クレカのみ(2026-09-08): 引き落とし日が土日祝のとき */
  debitShift: HolidayShift;
};

/** 日の選択肢(1〜30日と「末日」) */
const DAY_OPTIONS = [...Array.from({ length: 30 }, (_, i) => String(i + 1)), '31'];
const dayLabel = (d: string) => (d === '31' ? '末日' : `${d}日`);

export default function PaymentAccountsTab() {
  const toast = useToast();
  const { companies, defaultCompanyId, companyName } = useMasters();
  const [rows, setRows] = useState<CashAccount[]>([]);
  const [balances, setBalances] = useState<Map<string, Yen>>(new Map());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [edit, setEdit] = useState<EditState | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchCashAccounts();
      setRows(list);
      // 残高 = 期首+確定入出金。CF閲覧権限がない場合は取得できないため期首残高のまま
      const map = new Map<string, Yen>();
      await Promise.all(
        list.map(async (a) => {
          try {
            map.set(a.id, calcBalance(a, await fetchCashEntries(a.id, 1000)));
          } catch {
            map.set(a.id, a.opening_balance);
          }
        }),
      );
      setBalances(map);
    } catch (e) {
      toast(e instanceof Error ? e.message : '取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  function toggle(companyId: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  }

  async function save() {
    if (!edit) return;
    if (!edit.name.trim()) {
      toast('名称を入力してください');
      return;
    }
    const digits = edit.openingText.replace(/[,，\s]/g, '');
    const opening = /^\d+$/.test(digits) ? Number(digits) : digits === '' ? 0 : null;
    if (opening === null) {
      toast('期首残高を正しく入力してください(0以上の整数)');
      return;
    }
    // クレカは締め日・引き落とし日を持つ(2026-09-04)。クレカ以外は空にする
    const isCard = edit.kind === 'card';
    const closingDay = isCard && edit.closingDay ? Number(edit.closingDay) : null;
    const debitDay = isCard && edit.debitDay ? Number(edit.debitDay) : null;
    const debitOffset = isCard ? (Number(edit.debitOffset) === 2 ? 2 : 1) : null;
    const debitShift = isCard ? edit.debitShift : null;
    setBusy(true);
    try {
      if (edit.id === null) {
        const maxOrder = rows.reduce((m, r) => Math.max(m, r.display_order ?? 0), 0);
        await createCashAccount(
          {
            name: edit.name,
            kind: edit.kind,
            companyId: edit.companyId,
            openingBalance: opening,
            closingDay,
            debitDay,
            debitMonthOffset: debitOffset,
            debitHolidayShift: debitShift,
          },
          maxOrder + 1,
        );
        toast(`「${edit.name.trim()}」を追加しました`);
      } else {
        await updateCashAccount(edit.id, {
          name: edit.name.trim(),
          kind: edit.kind,
          company_id: edit.companyId,
          opening_balance: opening,
          active: edit.active,
          closing_day: closingDay,
          debit_day: debitDay,
          debit_month_offset: debitOffset,
          debit_holiday_shift: debitShift,
        });
        toast('保存しました');
      }
      setEdit(null);
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  // 登録がある会社を表示(会社マスタの並び順。未登録会社は枠を出さない)
  const companiesWithRows = companies.filter((c) => rows.some((r) => r.company_id === c.id));

  return (
    <>
      <div className="card" style={{ padding: '14px 22px' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          支払い方法(現金・銀行口座・クレカ)
          <button
            className="btn-green"
            style={{ marginLeft: 'auto', marginTop: 0 }}
            onClick={() =>
              setEdit({
                id: null,
                companyId: defaultCompanyId,
                kind: 'bank',
                name: '',
                openingText: '0',
                active: true,
                closingDay: '',
                debitOffset: '1',
                debitDay: '',
                debitShift: 'after',
              })
            }
          >
            新規追加
          </button>
        </h3>
        <p className="hint" style={{ margin: 0 }}>
          ここで登録した支払い方法が、支払い時のポップアップで選べます(銀行・現金はCFの残高管理にも使用)。
          会社ごとの「+/−」で開閉できます。削除は履歴を守るため「無効」で行います(編集から設定)。
        </p>
      </div>

      {loading ? (
        <div className="card">
          <p className="empty">読み込み中…</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="card">
          <p className="empty">まだ登録がありません。「新規追加」から登録してください。</p>
        </div>
      ) : (
        companiesWithRows.map((c) => {
          const list = rows
            .filter((r) => r.company_id === c.id)
            .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
          const open = !collapsed.has(c.id);
          return (
            <div className="card" key={c.id} style={{ padding: '12px 22px' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: open ? 10 : 0 }}>
                <button
                  className="txn-link"
                  style={{ fontWeight: 700, width: 18 }}
                  title={open ? '閉じる' : '開く'}
                  onClick={() => toggle(c.id)}
                >
                  {open ? '−' : '+'}
                </button>
                {c.name}
                <span className="hint" style={{ fontWeight: 400 }}>({list.length}件)</span>
              </h3>
              {open && (
                <table>
                  <thead>
                    <tr>
                      <th>会社</th>
                      <th>種別</th>
                      <th>名称</th>
                      <th className="num">残高</th>
                      <th>状態</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((r) => (
                      <tr key={r.id} style={{ opacity: r.active ? 1 : 0.5 }}>
                        <td className="hint">{companyName(r.company_id)}</td>
                        <td>
                          <span className="badge b-plan">{CASH_ACCOUNT_KIND_LABELS[r.kind] ?? r.kind}</span>
                        </td>
                        <td>
                          {r.name}
                          {/* クレカは会社ごと・カードごとに締め日・引き落とし日を持つ。未設定は目立たせる(2026-09-07) */}
                          {/* 同じ行に出す(2026-09-08 指示: 2行にしない) */}
                          {r.kind === 'card' &&
                            (debitRuleLabel(r) === '未設定' ? (
                              <span className="badge b-pend" style={{ marginLeft: 6 }}>締め日・引き落とし日 未設定</span>
                            ) : (
                              <span className="hint" style={{ marginLeft: 6, whiteSpace: 'nowrap' }}>{debitRuleLabel(r)}・{debitShiftLabel(r.debit_holiday_shift)}</span>
                            ))}
                        </td>
                        <td className="num">{formatYen(balances.get(r.id) ?? r.opening_balance)}</td>
                        <td>
                          <span className={`badge ${r.active ? 'b-ok' : 'b-ng'}`}>{r.active ? '有効' : '無効'}</span>
                        </td>
                        <td>
                          <button
                            className="txn-link"
                            onClick={() =>
                              setEdit({
                                id: r.id,
                                companyId: r.company_id,
                                kind: r.kind,
                                name: r.name,
                                openingText: formatAmountInput(String(r.opening_balance)),
                                active: r.active,
                                closingDay: r.closing_day ? String(r.closing_day) : '',
                                debitOffset: r.debit_month_offset === 2 ? '2' : '1',
                                debitDay: r.debit_day ? String(r.debit_day) : '',
                                debitShift: r.debit_holiday_shift ?? 'none',
                              })
                            }
                          >
                            編集
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })
      )}

      {/* 新規追加・編集(ポップアップ) */}
      {edit && (
        <Modal onClose={() => setEdit(null)} width="min(560px, 92vw)">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <h3>{edit.id === null ? '支払い方法を新規追加' : `編集 — ${edit.name}`}</h3>
          </div>
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <div>
              <label>会社</label>
              <select value={edit.companyId} onChange={(e) => setEdit({ ...edit, companyId: e.target.value })}>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>種別</label>
              <select value={edit.kind} onChange={(e) => setEdit({ ...edit, kind: e.target.value as CashAccountKind })}>
                {CASH_ACCOUNT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {CASH_ACCOUNT_KIND_LABELS[k]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>名称(例: SC現金 / みずほ銀行 / 楽天カード)</label>
              <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="例)SC現金" />
            </div>
            <div>
              <label>期首残高(円・クレカは0でOK)</label>
              <input
                inputMode="numeric"
                value={edit.openingText}
                onChange={(e) => setEdit({ ...edit, openingText: e.target.value })}
              />
            </div>
            {/* クレカの締め日・引き落とし日(2026-09-04 指示)。支払い入力で引き落とし日を自動表示するのに使う */}
            {edit.kind === 'card' && (
              <>
                <div>
                  <label>締め日</label>
                  <select value={edit.closingDay} onChange={(e) => setEdit({ ...edit, closingDay: e.target.value })}>
                    <option value="">未設定</option>
                    {DAY_OPTIONS.map((d) => (
                      <option key={d} value={d}>
                        {dayLabel(d)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label>引き落とし日</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <select
                      value={edit.debitOffset}
                      onChange={(e) => setEdit({ ...edit, debitOffset: e.target.value === '2' ? '2' : '1' })}
                    >
                      <option value="1">翌月</option>
                      <option value="2">翌々月</option>
                    </select>
                    <select value={edit.debitDay} onChange={(e) => setEdit({ ...edit, debitDay: e.target.value })}>
                      <option value="">未設定</option>
                      {DAY_OPTIONS.map((d) => (
                        <option key={d} value={d}>
                          {dayLabel(d)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="hint" style={{ marginTop: 3 }}>
                    例: 15日締め・翌月10日引き落とし。支払い入力で「引き落とし日」が自動で出ます
                  </div>
                </div>
                <div>
                  <label>引き落とし日が土日祝のとき</label>
                  <select value={edit.debitShift} onChange={(e) => setEdit({ ...edit, debitShift: e.target.value as HolidayShift })}>
                    <option value="after">後の平日にする</option>
                    <option value="before">前の平日にする</option>
                    <option value="none">そのまま</option>
                  </select>
                  <div className="hint" style={{ marginTop: 3 }}>経費申請・繰り返し支払いの引き落とし日の計算に使います(2026-09-08)</div>
                </div>
              </>
            )}
            <div>
              <label>状態(無効にすると選択肢に出なくなります)</label>
              <select
                value={edit.active ? '1' : '0'}
                onChange={(e) => setEdit({ ...edit, active: e.target.value === '1' })}
              >
                <option value="1">有効</option>
                <option value="0">無効</option>
              </select>
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn-green" disabled={busy || !edit.name.trim()} onClick={() => void save()}>
              {busy ? '保存中…' : edit.id === null ? '追加する' : '保存'}
            </button>
            <button className="btn-line" onClick={() => setEdit(null)} disabled={busy}>
              キャンセル
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
